import { useState, useEffect } from "react";
import {
  useForm,
  FormProvider,
  SubmitHandler,
  useFieldArray,
} from "react-hook-form";
import { useAppDispatch } from "../../../../stores/hooks/hooks";
import { updateCreatorBriefThunk } from "../../../../stores/thunks/creatorBriefsThunk";
import { useAppSelector } from "../../../../stores/hooks/hooks";
import {
  RevButton,
  RevCard,
  RevTextArea,
  RevUnsavedChangesDialog,
} from "@revmatics/catalyst";
import { DetailSectionCard } from "./DetailSectionCard";
import { TextAreaListItem } from "./TextAreaListItem";
import {
  CreatorBrief,
  UpdateCreatorBriefPayload,
  UsagePermissionTerminology,
} from "../../../../stores/types/creatorBriefsTypes";

// TODO: comment back in when data regression is resolved
// import { faArrowUpRightFromSquare } from "@fortawesome/pro-light-svg-icons";
import brandAssets from "/images/icons/brand_assets.svg";
import brandGuidelines from "/images/icons/brand_guidelines.svg";
import campaignStory from "/images/icons/campaign_story.svg";
import contentRequirements from "/images/icons/content_requirements.svg";
import creativeConcept from "/images/icons/creative_concept.svg";
import personaDetails from "/images/icons/persona_details.svg";
import usagePermission from "/images/icons/usage_permission.svg";
import { SelectBrandAssetsDialog } from "../SelectBrandAssetsDialog";
import { displayAlert } from "../../../../stores/slice/alertSlice";
import { useUnsavedChangesDialog } from "../../../../helpers/hooks/useUnsavedChangesDialog.tsx";
import { useNavigate } from "react-router";
import { useTrimmedRegister } from "../../../../helpers/hooks/useTrimmedRegister.ts";
import { useFlags } from "launchdarkly-react-client-sdk";

type CreatorBriefFormValues = Omit<
  CreatorBrief,
  "key_insights" | "creative_approach"
> & {
  key_insights: { value: string }[];
  creative_approach: {
    approaches: { id?: string; approach: string }[];
  };
};

type Props = {
  brief: CreatorBrief;
};

export const BriefDetailsTab = ({ brief }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isAssetDialogOpen, setIsAssetDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [href, setHref] = useState("");
  const [isOpenUnsavedModal, setOpenUnsavedModal] = useState(false);
  const [editModes, setEditModes] = useState({
    campaignStory: false,
    brandGuidelines: false,
    creativeConcept: false,
    usagePermission: false,
  });
  const { brandDetails: ffBrandDetails } = useFlags();
  const isAnySectionInEditMode = Object.values(editModes).some(
    (isEditing) => isEditing,
  );
  const brandId = useAppSelector((state) => state.brand.currentBrand);

  const methods = useForm<CreatorBriefFormValues>({
    mode: "onSubmit",
  });
  const {
    handleSubmit,
    reset,
    register,
    control,
    formState: { dirtyFields },
  } = methods;

  const {
    fields: keyInsightsFields,
    append: appendKeyInsight,
    remove: removeKeyInsight,
  } = useFieldArray({ control, name: "key_insights" });

  const {
    fields: approachesFields,
    append: appendApproach,
    remove: removeApproach,
  } = useFieldArray({ control, name: "creative_approach.approaches" });

  const transformBriefToFormValues = (
    briefToTransform: CreatorBrief,
  ): CreatorBriefFormValues => {
    const keyInsightsArray = (briefToTransform.key_insights ?? []).map(
      (insight) => ({ value: insight }),
    );

    const approachesArray =
      briefToTransform.creative_approach?.approaches ?? [];

    return {
      ...briefToTransform,
      key_insights:
        keyInsightsArray.length > 0 ? keyInsightsArray : [{ value: "" }],
      creative_approach: {
        approaches:
          approachesArray.length > 0 ? approachesArray : [{ approach: "" }],
      },
    };
  };

  useEffect(() => {
    reset(transformBriefToFormValues(brief));
  }, [brief, reset]);

  const handleToggleEdit = (sectionName: keyof typeof editModes) => {
    setEditModes((prevModes) => ({
      ...prevModes,
      [sectionName]: !prevModes[sectionName],
    }));
  };

  const hasUnsavedChanges =
    Object.keys(dirtyFields).length > 0 && isAnySectionInEditMode;

  useUnsavedChangesDialog({
    hasUnsavedChanges,
    onNavigateAttempt: (href) => {
      setHref(href);
      setOpenUnsavedModal(true);
    },
  });

  const handleConfirmDiscard = () => {
    setEditModes({
      campaignStory: false,
      brandGuidelines: false,
      creativeConcept: false,
      usagePermission: false,
    });
    setOpenUnsavedModal(false);
    if (href) {
      navigate(href);
    } else {
      navigate(-1);
    }
    setHref("");
  };

  const onSubmit: SubmitHandler<CreatorBriefFormValues> = async (data) => {
    setIsSubmitting(true);
    const payloadForApi: UpdateCreatorBriefPayload = {};

    if (dirtyFields.objective) {
      payloadForApi.objective = data.objective;
    }
    if (dirtyFields.core_message)
      payloadForApi.core_message = data.core_message;
    if (dirtyFields.brand_positioning)
      payloadForApi.brand_positioning = data.brand_positioning;
    if (dirtyFields.tagline) payloadForApi.tagline = data.tagline;
    if (dirtyFields.key_insights) {
      payloadForApi.key_insights = data.key_insights
        .map((insight) => insight.value)
        .filter((value) => value.trim() !== "");
    }
    if (dirtyFields.creative_approach) {
      payloadForApi.creative_approach = {
        approaches: data.creative_approach.approaches.filter(
          (item) => item.approach.trim() !== "",
        ),
      };
    }
    if (dirtyFields.usage_permission_and_brand_exclusivity) {
      payloadForApi.usage_permission_and_brand_exclusivity =
        data.usage_permission_and_brand_exclusivity;
    }

    dispatch(
      updateCreatorBriefThunk({
        id: brief.id,
        data: payloadForApi,
        callback: () => {
          if (href) {
            navigate(href);
          }
        },
      }),
    )
      .unwrap()
      .then((updatedBrief) => {
        reset(transformBriefToFormValues(updatedBrief));
      })
      .catch((err) => {
        console.error("Failed to update brief details", err);
        dispatch(
          displayAlert({
            type: "error",
            text: "Failed to update brief details",
          }),
        );
      })
      .finally(() => {
        const activeSection = Object.keys(editModes).find(
          (key) => editModes[key as keyof typeof editModes],
        );
        if (activeSection) {
          handleToggleEdit(activeSection as keyof typeof editModes);
        }
        setIsSubmitting(false);
      });
  };

  const onCancel = (sectionName: keyof typeof editModes) => {
    if (hasUnsavedChanges) {
      setOpenUnsavedModal(true);
      return;
    }
    reset(transformBriefToFormValues(brief));
    handleToggleEdit(sectionName);
  };

  const formatTerminologyKey = (key: string) => {
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const registerTrimmed = useTrimmedRegister(register);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          <RevCard padding="p-6">
            <DetailSectionCard
              icon={campaignStory}
              title="Campaign Story"
              canEdit
              isEditMode={editModes.campaignStory}
              onToggleEdit={() => handleToggleEdit("campaignStory")}
              onCancel={() => onCancel("campaignStory")}
              isEditDisabled={
                isAnySectionInEditMode && !editModes.campaignStory
              }
              isEditLoading={isSubmitting}
            >
              {!editModes.campaignStory ? (
                <div>
                  <p className="typography-body-sm text-monochrome-900">
                    <b>Campaign Objective:</b>
                  </p>
                  <p className="typography-body-sm text-monochrome-900 mb-2">
                    {brief.objective || ""}
                  </p>
                  <p className="typography-body-sm text-monochrome-900">
                    <b>Key Message:</b>
                  </p>
                  <p className="typography-body-sm text-monochrome-900">
                    {brief.core_message || ""}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <RevTextArea
                    label="Campaign Objective"
                    name="objective"
                    isLabelBold
                    rows={4}
                    register={registerTrimmed("objective")}
                    useLength={false}
                  />
                  <RevTextArea
                    label="Key Message"
                    name="core_message"
                    isLabelBold
                    rows={4}
                    register={registerTrimmed("core_message")}
                    useLength={false}
                  />
                </div>
              )}
            </DetailSectionCard>
          </RevCard>

          <RevCard padding="p-6">
            <DetailSectionCard
              icon={brandGuidelines}
              title="Brand Guidelines"
              canEdit
              isEditMode={editModes.brandGuidelines}
              onToggleEdit={() => handleToggleEdit("brandGuidelines")}
              onCancel={() => onCancel("brandGuidelines")}
              isEditDisabled={
                isAnySectionInEditMode && !editModes.brandGuidelines
              }
            >
              {!editModes.brandGuidelines ? (
                <div>
                  <p className="typography-body-sm text-monochrome-900">
                    <b>Brand Positioning:</b>
                  </p>
                  <p className="typography-body-sm text-monochrome-900 mb-2">
                    {brief.brand_positioning || ""}
                  </p>
                  <p className="typography-body-sm text-monochrome-900">
                    <b>Tagline:</b>
                  </p>
                  <p className="typography-body-sm text-monochrome-900">
                    {brief.tagline || ""}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <RevTextArea
                    label="Brand Positioning"
                    name="brand_positioning"
                    isLabelBold
                    rows={4}
                    register={registerTrimmed("brand_positioning")}
                    useLength={false}
                  />
                  <RevTextArea
                    label="Tagline"
                    name="tagline"
                    isLabelBold
                    rows={4}
                    register={registerTrimmed("tagline")}
                    useLength={false}
                  />
                </div>
              )}
            </DetailSectionCard>
          </RevCard>

          <RevCard padding="p-6">
            <DetailSectionCard
              icon={brandAssets}
              title="Brand Assets"
              isEditLoading={isSubmitting}
            >
              <p className="typography-body-sm text-monochrome-900 mb-3">
                {brief.brandassets?.length ?? 0} items selected
              </p>
              {brief.brandassets && brief.brandassets.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {brief.brandassets.map((asset) => {
                    const previewSrc = asset.image || asset.thumbnail_url || "";

                    return previewSrc ? (
                      <img
                        key={asset.id}
                        src={previewSrc}
                        alt={asset.name || `Brand Asset ${asset.id}`}
                        className="w-10 h-10 rounded-md object-cover"
                      />
                    ) : (
                      <div
                        key={asset.id}
                        className="w-10 h-10 rounded-md bg-monochrome-50 border border-monochrome-200 flex items-center justify-center"
                        title={asset.name || `Brand Asset ${asset.id}`}
                      >
                        <span className="typography-body-xs text-monochrome-500">
                          N/A
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              <RevButton
                text="Select Brand Assets"
                variant="secondary"
                handleClick={() => setIsAssetDialogOpen(true)}
                className="text-nowrap"
              />
            </DetailSectionCard>
          </RevCard>

          <RevCard padding="p-6">
            <DetailSectionCard
              icon={personaDetails}
              title="Persona Details"
              editSearch={`personaId=${brief.persona.id}`}
              editPathname=""
            >
              <div className="flex flex-col gap-1">
                <p className="typography-body-sm text-monochrome-900">
                  <b>Target audience:</b>
                </p>
                <div className="flex flex-col gap-3">
                  {Object.entries(brief.target_audience ?? {}).map(
                    ([key, value]) =>
                      value && (
                        <p
                          key={key}
                          className="typography-body-sm text-monochrome-900"
                        >
                          {value}
                        </p>
                      ),
                  )}
                </div>
              </div>
            </DetailSectionCard>
          </RevCard>

          <RevCard padding="p-6">
            <DetailSectionCard
              icon={creativeConcept}
              title="Creative Concept Ideas"
              canEdit
              isEditMode={editModes.creativeConcept}
              onToggleEdit={() => handleToggleEdit("creativeConcept")}
              onCancel={() => onCancel("creativeConcept")}
              isEditDisabled={
                isAnySectionInEditMode && !editModes.creativeConcept
              }
              isEditLoading={isSubmitting}
            >
              <p className="typography-body-sm text-monochrome-900 mb-3">
                Please use these suggestions to develop unique, engaging content
                that resonates with the campaign's message.
              </p>
              {!editModes.creativeConcept ? (
                <div>
                  <p className="typography-body-sm text-monochrome-900 mb-1">
                    <b>Key Insights:</b>
                  </p>
                  <ul className="list-disc list-inside space-y-1 mb-3">
                    {(brief.key_insights ?? []).map((insight, index) => (
                      <li
                        key={index}
                        className="typography-body-sm text-monochrome-900"
                      >
                        {insight}
                      </li>
                    ))}
                  </ul>

                  <p className="typography-body-sm text-monochrome-900 mb-1">
                    <b>Approaches:</b>
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    {(brief.creative_approach?.approaches ?? []).map((item) => (
                      <li
                        key={item.approach}
                        className="typography-body-sm text-monochrome-900"
                      >
                        {item.approach}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div>
                  <div className="mb-6">
                    <p className="typography-body-sm text-monochrome-900 mb-1">
                      <b>Key Insights:</b>
                    </p>
                    <div className="flex flex-col gap-4">
                      {keyInsightsFields.map((field, index) => (
                        <TextAreaListItem
                          key={field.id}
                          isLastItem={keyInsightsFields.length - 1 === index}
                          handleDeleteItem={() => removeKeyInsight(index)}
                          name={`key_insights.${index}.value`}
                          control={control}
                          handleAddItem={() => appendKeyInsight({ value: "" })}
                          addItemText={"Insight"}
                        >
                          <RevTextArea
                            label=""
                            name={`key_insights.${index}.value`}
                            rows={3}
                            register={registerTrimmed(
                              `key_insights.${index}.value`,
                            )}
                            useLength={false}
                            placeholder={"Enter key insight"}
                          />
                        </TextAreaListItem>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="typography-body-sm text-monochrome-900 mb-1">
                      <b>Approaches:</b>
                    </p>
                    <div className="flex flex-col gap-4">
                      {approachesFields.map((field, index) => (
                        <TextAreaListItem
                          key={field.id}
                          isLastItem={approachesFields.length - 1 === index}
                          handleDeleteItem={() => removeApproach(index)}
                          name={`creative_approach.approaches.${index}.approach`}
                          control={control}
                          handleAddItem={() => appendApproach({ approach: "" })}
                          addItemText={"Approach"}
                        >
                          <RevTextArea
                            label=""
                            name={`creative_approach.approaches.${index}.approach`}
                            rows={3}
                            register={registerTrimmed(
                              `creative_approach.approaches.${index}.approach`,
                            )}
                            useLength={false}
                            placeholder={"Enter approach"}
                          />
                        </TextAreaListItem>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </DetailSectionCard>
          </RevCard>

          <RevCard padding="p-6">
            <DetailSectionCard
              icon={contentRequirements}
              title="Content Requirements"
              editPathname={
                ffBrandDetails
                  ? `/company-settings/brand/${brandId}`
                  : undefined
              }
              editSearch={`tab=brand_guidelines&subtab=dos_and_donts`}
              isEditLoading={isSubmitting}
            >
              <div>
                <p className="typography-body-sm mb-3">
                  Please carefully review the requirements below before creating
                  content to help avoid the need for reshoots.
                </p>
                <p className="typography-body-sm mb-1">
                  <b>Content Do's:</b>
                </p>
                <ul className="list-disc list-inside space-y-1 mb-3">
                  {(brief.content_requirements?.do ?? []).map((item, index) => (
                    <li
                      key={index}
                      className="typography-body-sm text-monochrome-900"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="typography-body-sm mb-1">
                  <b>Content Don'ts:</b>
                </p>
                <ul className="list-disc list-inside space-y-1">
                  {(brief.content_requirements?.dont ?? []).map(
                    (item, index) => (
                      <li
                        key={index}
                        className="typography-body-sm text-monochrome-900"
                      >
                        {item}
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </DetailSectionCard>
          </RevCard>

          <RevCard padding="p-6">
            <DetailSectionCard
              icon={usagePermission}
              title="Usage Permission and Brand Exclusivity"
              canEdit
              isEditMode={editModes.usagePermission}
              onToggleEdit={() => handleToggleEdit("usagePermission")}
              onCancel={() => onCancel("usagePermission")}
              isEditDisabled={
                (isAnySectionInEditMode && !editModes.usagePermission) ||
                Object.keys(
                  brief.usage_permission_and_brand_exclusivity?.terminology ??
                    {},
                ).length === 0
              }
              isEditLoading={isSubmitting}
            >
              <p className="typography-body-sm text-monochrome-900 mb-3">
                {brief.usage_permission_and_brand_exclusivity?.description ||
                  ""}
              </p>
              {!editModes.usagePermission ? (
                <div>
                  <p className="typography-body-sm text-monochrome-900 mb-1">
                    <b>Terminology:</b>
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    {Object.entries(
                      brief.usage_permission_and_brand_exclusivity
                        ?.terminology ?? {},
                    ).map(([key, value]) => (
                      <li
                        key={key}
                        className="typography-body-sm text-monochrome-900"
                      >
                        <b>{formatTerminologyKey(key)}:</b> {value}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div>
                  <p className="typography-body-sm text-monochrome-900 mb-2">
                    <b>Terminology:</b>
                  </p>
                  <div className="flex flex-col gap-2">
                    {Object.entries(
                      brief.usage_permission_and_brand_exclusivity
                        ?.terminology ?? {},
                    ).map(([key, value]) => (
                      <RevTextArea
                        label={`${formatTerminologyKey(key)}:`}
                        key={key}
                        name={`usage_permission_and_brand_exclusivity.terminology.${key}`}
                        rows={3}
                        register={registerTrimmed(
                          `usage_permission_and_brand_exclusivity.terminology.${key as keyof UsagePermissionTerminology}`,
                        )}
                        defaultValue={value}
                        useLength={false}
                        isLabelBold
                      />
                    ))}
                  </div>
                </div>
              )}
            </DetailSectionCard>
          </RevCard>
        </div>

        <SelectBrandAssetsDialog
          isOpen={isAssetDialogOpen}
          setIsOpen={setIsAssetDialogOpen}
          brief={brief}
        />
        <RevUnsavedChangesDialog
          pageId="brief-details-page"
          isOpen={isOpenUnsavedModal}
          setIsOpen={(open) => {
            if (!open) setHref("");
            setOpenUnsavedModal(open);
          }}
          onSave={handleSubmit(onSubmit)}
          onDiscard={handleConfirmDiscard}
          isLoading={isSubmitting}
        />
      </form>
    </FormProvider>
  );
};
